import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import guardianPlacementService from '../services/guardianPlacementService';
import styles from '../assets/guardianPlacement.module.css';

const GuardianPlacement = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    property: null,
    guardian: null,
    secondGuardian: null,
    room: null,
    availableRooms: [],
    availableGuardians: [],
    paymentLinks: null,
    qrCodes: null
  });
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedSecondGuardianId, setSelectedSecondGuardianId] = useState('');
  const [paymentSplit, setPaymentSplit] = useState('100:0');
  const [isSharedPlacement, setIsSharedPlacement] = useState(false);

  const navigate = useNavigate();
  const { propertyId, guardianId, roomId } = useParams();
  const location = window.location;

  useEffect(() => {
    if (propertyId && guardianId) {
      // Check if we're accessing step2 directly
      if (location.pathname.includes('/step2/') && roomId) {
        loadStep2Data();
      } else {
        loadStep1Data();
      }
    }
  }, [propertyId, guardianId, roomId]);

  const loadStep1Data = async () => {
    try {
      setLoading(true);
      const response = await guardianPlacementService.getStep1Data(propertyId, guardianId);

      if (response.success) {
        setData(prev => ({
          ...prev,
          property: response.data.property,
          guardian: response.data.guardian,
          availableRooms: response.data.availableRooms
        }));
      } else {
        toast.error('Failed to load placement data');
      }
    } catch (error) {
      console.error('Error loading step 1 data:', error);
      toast.error('Failed to load placement data');
    } finally {
      setLoading(false);
    }
  };

  const loadStep2Data = async () => {
    try {
      setLoading(true);
      const response = await guardianPlacementService.getStep2Data(propertyId, guardianId, roomId);

      if (response.success) {
        setData(prev => ({
          ...prev,
          property: response.data.property,
          guardian: response.data.guardian,
          room: response.data.room,
          secondGuardian: response.data.secondGuardian || null
        }));
        setSelectedRoomId(roomId);
        setStep(2);
      } else {
        toast.error('Failed to load step 2 data');
      }
    } catch (error) {
      console.error('Error loading step 2 data:', error);
      toast.error('Failed to load step 2 data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableGuardians = async () => {
    try {
      setLoading(true);
      const response = await guardianPlacementService.getAvailableGuardians(propertyId, guardianId, selectedRoomId);

      if (response.success) {
        setData(prev => ({
          ...prev,
          availableGuardians: response.data.availableGuardians
        }));
      } else {
        toast.error('Failed to load available guardians');
      }
    } catch (error) {
      console.error('Error loading guardians:', error);
      toast.error('Failed to load available guardians');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSelection = () => {
    if (!selectedRoomId) {
      toast.error('Please select a room');
      return;
    }

    const selectedRoom = data.availableRooms.find(room => room.id === parseInt(selectedRoomId));
    setData(prev => ({ ...prev, room: selectedRoom }));

    if (isSharedPlacement) {
      setStep(1.5); // Step 1b - select second guardian
      loadAvailableGuardians();
    } else {
      setStep(2);
    }
  };

  const handleSecondGuardianSelection = () => {
    if (!selectedSecondGuardianId) {
      toast.error('Please select a second guardian');
      return;
    }

    const secondGuardian = data.availableGuardians.find(g => g.id === parseInt(selectedSecondGuardianId));
    setData(prev => ({ ...prev, secondGuardian }));
    setStep(2);
  };

  const generatePaymentLinks = async () => {
    try {
      setLoading(true);
      const payload = {
        secondGuardianId: selectedSecondGuardianId || null,
        paymentSplit: paymentSplit
      };

      const response = await guardianPlacementService.generatePaymentLinks(
        propertyId,
        guardianId,
        selectedRoomId,
        payload
      );

      if (response.success) {
        setData(prev => ({
          ...prev,
          paymentLinks: {
            link1: response.data.link1,
            link2: response.data.link2
          },
          qrCodes: {
            qrCode1: response.data.qrCode1,
            qrCode2: response.data.qrCode2
          }
        }));
        setStep(2);
      } else {
        toast.error('Failed to generate payment links');
      }
    } catch (error) {
      console.error('Error generating payment links:', error);
      toast.error('Failed to generate payment links');
    } finally {
      setLoading(false);
    }
  };

  const completePlacement = async () => {
    try {
      setLoading(true);
      const payload = {
        secondGuardianId: selectedSecondGuardianId || null
      };

      const response = await guardianPlacementService.completePlacement(
        propertyId,
        guardianId,
        selectedRoomId,
        payload
      );

      if (response.success) {
        toast.success('Guardian placement completed successfully!');
        setStep(3);
      } else {
        toast.error(response.message || 'Failed to complete placement');
      }
    } catch (error) {
      console.error('Error completing placement:', error);
      toast.error('Failed to complete placement');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className={styles.placementStep}>
      <div className={styles.stepHeader}>
        <h2>Step 1: Select Room</h2>
        <p>Choose a room for guardian placement</p>
      </div>

      {data.property && (
        <div className={`${styles.propertyInfo} ${styles.card}`}>
          <h3>{data.property.name}</h3>
          <p>{data.property.address}</p>
          <p>Postcode: {data.property.postcode}</p>
        </div>
      )}

      {data.guardian && (
        <div className={`${styles.guardianInfo} ${styles.card}`}>
          <h3>Guardian: {data.guardian.user?.first_name} {data.guardian.user?.last_name}</h3>
          <p>Email: {data.guardian.user?.email}</p>
          <p>Status: {data.guardian.status_name}</p>
        </div>
      )}

      <div className={`${styles.roomSelection} ${styles.card}`}>
        <h3>Available Rooms</h3>
        <div className={styles.formGroup}>
          <select
            value={selectedRoomId}
            onChange={(e) => setSelectedRoomId(e.target.value)}
            className={styles.formControl}
          >
            <option value="">Select a room...</option>
            {data.availableRooms.map(room => (
              <option key={room.id} value={room.id}>
                Room {room.room_number} - £{room.price}/month - {room.room_type}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <div className={styles.formCheck}>
            <input
              type="checkbox"
              id="sharedPlacement"
              checked={isSharedPlacement}
              onChange={(e) => setIsSharedPlacement(e.target.checked)}
              className={styles.formCheckInput}
            />
            <label htmlFor="sharedPlacement" className={styles.formCheckLabel}>
              Shared accommodation (2 guardians)
            </label>
          </div>
        </div>

        <button
          onClick={handleRoomSelection}
          disabled={!selectedRoomId || loading}
          className={`${styles.btn} ${styles.btnPrimary}`}
        >
          {loading ? 'Loading...' : 'Continue'}
        </button>
      </div>
    </div>
  );

  const renderStep1b = () => (
    <div className={styles.placementStep}>
      <div className={styles.stepHeader}>
        <h2>Step 1b: Select Second Guardian</h2>
        <p>Choose a second guardian for shared accommodation</p>
      </div>

      <div className={`${styles.guardianSelection} ${styles.card}`}>
        <h3>Available Guardians</h3>
        <div className={styles.formGroup}>
          <select
            value={selectedSecondGuardianId}
            onChange={(e) => setSelectedSecondGuardianId(e.target.value)}
            className={styles.formControl}
          >
            <option value="">Select second guardian...</option>
            {data.availableGuardians.map(guardian => (
              <option key={guardian.id} value={guardian.id}>
                {guardian.user?.first_name} {guardian.user?.last_name} - {guardian.user?.email}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Payment Split</label>
          <select
            value={paymentSplit}
            onChange={(e) => setPaymentSplit(e.target.value)}
            className={styles.formControl}
          >
            <option value="50:50">50:50 Split</option>
            <option value="60:40">60:40 Split</option>
            <option value="70:30">70:30 Split</option>
            <option value="80:20">80:20 Split</option>
          </select>
        </div>

        <div className={styles.buttonGroup}>
          <button
            onClick={() => setStep(1)}
            className={`${styles.btn} ${styles.btnSecondary}`}
          >
            Back
          </button>
          <button
            onClick={handleSecondGuardianSelection}
            disabled={!selectedSecondGuardianId || loading}
            className={`${styles.btn} ${styles.btnPrimary}`}
          >
            {loading ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={styles.placementStep}>
      <div className={styles.stepHeader}>
        <h2>Step 2: Payment Links</h2>
        <p>Generate and send payment links to guardians</p>
      </div>

      <div className={`${styles.placementSummary} ${styles.card}`}>
        <h3>Placement Summary</h3>
        <p><strong>Property:</strong> {data.property?.name}</p>
        <p><strong>Room:</strong> Room {data.room?.room_number} - £{data.room?.price}/month</p>
        <p><strong>Primary Guardian:</strong> {data.guardian?.user?.first_name} {data.guardian?.user?.last_name}</p>
        {data.secondGuardian && (
          <p><strong>Second Guardian:</strong> {data.secondGuardian.user?.first_name} {data.secondGuardian.user?.last_name}</p>
        )}
      </div>

      <div className={`${styles.paymentLinks} ${styles.card}`}>
        <h3>Payment Links & QR Codes</h3>
        <button
          onClick={generatePaymentLinks}
          disabled={loading}
          className={`${styles.btn} ${styles.btnPrimary} ${styles.mb3}`}
        >
          {loading ? 'Generating...' : 'Generate Payment Links'}
        </button>

        {data.paymentLinks && (
          <div className={styles.linksContainer}>
            <div className={styles.paymentLinkSection}>
              <h4>Guardian 1: {data.guardian?.user?.first_name} {data.guardian?.user?.last_name}</h4>
              <div className={styles.linkDetails}>
                <p><strong>Payment Link:</strong></p>
                <input
                  type="text"
                  value={data.paymentLinks.link1}
                  readOnly
                  className={`${styles.formControl} ${styles.mb2}`}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(data.paymentLinks.link1)}
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnOutlinePrimary}`}
                >
                  Copy Link
                </button>
              </div>
              {data.qrCodes?.qrCode1 && (
                <div className={styles.qrCode}>
                  <p><strong>QR Code:</strong></p>
                  <img src={data.qrCodes.qrCode1} alt="Payment QR Code 1" />
                </div>
              )}
            </div>

            {data.paymentLinks.link2 && (
              <div className={styles.paymentLinkSection}>
                <h4>Guardian 2: {data.secondGuardian?.user?.first_name} {data.secondGuardian?.user?.last_name}</h4>
                <div className={styles.linkDetails}>
                  <p><strong>Payment Link:</strong></p>
                  <input
                    type="text"
                    value={data.paymentLinks.link2}
                    readOnly
                    className={`${styles.formControl} ${styles.mb2}`}
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(data.paymentLinks.link2)}
                    className={`${styles.btn} ${styles.btnSm} ${styles.btnOutlinePrimary}`}
                  >
                    Copy Link
                  </button>
                </div>
                {data.qrCodes?.qrCode2 && (
                  <div className={styles.qrCode}>
                    <p><strong>QR Code:</strong></p>
                    <img src={data.qrCodes.qrCode2} alt="Payment QR Code 2" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className={`${styles.completionSection} ${styles.mt4}`}>
          <p><em>Once all guardians have completed their payments, click "Complete Placement" to finalize the placement.</em></p>
          <button
            onClick={completePlacement}
            disabled={loading}
            className={`${styles.btn} ${styles.btnSuccess}`}
          >
            {loading ? 'Processing...' : 'Complete Placement'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={styles.placementStep}>
      <div className={styles.stepHeader}>
        <h2>Step 3: Placement Complete</h2>
        <p>Guardian placement has been completed successfully!</p>
      </div>

      <div className={`${styles.completionSummary} ${styles.card}`}>
        <div className={styles.successIcon}>
          <i className={`fa fa-check-circle ${styles.textSuccess}`} style={{fontSize: '48px'}}></i>
        </div>
        <h3>Placement Successful!</h3>
        <p>The guardian(s) have been successfully placed in the property.</p>

        <div className={styles.summaryDetails}>
          <p><strong>Property:</strong> {data.property?.name}</p>
          <p><strong>Room:</strong> Room {data.room?.room_number}</p>
          <p><strong>Guardian(s):</strong> {data.guardian?.user?.first_name} {data.guardian?.user?.last_name}
            {data.secondGuardian && ` & ${data.secondGuardian.user?.first_name} ${data.secondGuardian.user?.last_name}`}
          </p>
        </div>

        <div className={styles.nextActions}>
          <button
            onClick={() => navigate('/guardians')}
            className={`${styles.btn} ${styles.btnPrimary} ${styles.me2}`}
          >
            View Guardians
          </button>
          <button
            onClick={() => navigate('/properties')}
            className={`${styles.btn} ${styles.btnOutlinePrimary}`}
          >
            View Properties
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && step === 1) {
    return (
      <div className={styles.guardianPlacement}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinnerBorder} role="status">
            <span className={styles.srOnly}>Loading...</span>
          </div>
          <p>Loading placement data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.guardianPlacement}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className={styles.placementHeader}>
              <h1>Guardian Placement</h1>
              <div className={styles.stepIndicator}>
                <div className={`${styles.step} ${step >= 1 ? styles.active : ''} ${step > 1 ? styles.completed : ''}`}>1</div>
                <div className={styles.stepLine}></div>
                <div className={`${styles.step} ${step >= 2 ? styles.active : ''} ${step > 2 ? styles.completed : ''}`}>2</div>
                <div className={styles.stepLine}></div>
                <div className={`${styles.step} ${step >= 3 ? styles.active : ''}`}>3</div>
              </div>
            </div>

            {step === 1 && renderStep1()}
            {step === 1.5 && renderStep1b()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianPlacement;